import { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useQuery } from 'hooks/useCustomQuery';
import Button from '../../../components/v4/Button';
import Heading from '../../../components/v4/Heading';
import AddressFields from './AddressFields';
import AddressCustomAttributes from './AddressCustomAttributes';
import useAddress from '../../../hooks/useAddress';
import {
    Box,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    Checkbox,
    VStack,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
} from '@chakra-ui/react';
import { axios } from '../../../services/axios';
import Alert from '../../../components/v4/Alert';
import AlertDescription from '../../../components/v4/AlertDescription';
import AlertIcon from '../../../components/v4/AlertIcon';
import Text from '../../../components/v4/Text';
import Input from '../../../components/v4/Input';
import InputGroup from '../../../components/v4/InputGroup';
import InputRightElement from '../../../components/v4/InputRightElement';

import { usePlacesWidget } from 'react-google-autocomplete';

import { SearchIcon } from '@chakra-ui/icons';
import { useSelector } from 'react-redux';
import secret from '../../../secret.json';

import walkthroughIds from '../walkthroughIds';
import { toast } from 'react-toastify';
import { Card } from 'components/v4';
import { breadCrumbStyling } from 'components/forms/new/formStyles';
import { Link } from 'react-router-dom';
import {
    customTabStylesCombineTheme,
    selectedTabStylesCombineTheme,
} from '@/constants';

export default function AddressesCreate({ setAddressAction }) {
    const dispatch = useDispatch();
    const { addressFields, setAddressField, transformAddress } = useAddress();
    const companyID = useSelector(
        (state) => state.navigationLists.currentCompanyData?.id,
    );
    const companyName = useSelector(
        (state) => state.navigationLists.currentCompanyData?.name,
    );
    const { darkMode } = useSelector((state) => state.settings);
    const { requiredScope } = useSelector((state) => state.login);

    const { addresses } = walkthroughIds;

    const countriesRef = useRef([]);

    const { data: countriesRaw = [] } = useQuery({
        queryKey: ['countriesList'],
        queryFn: async () => {
            const res = await axios.get('/countries/list');
            return res.data;
        },
    });
    const countries = [
        { alpha2: '', name: 'Select Country', id: '' },
        ...countriesRaw,
    ];
    countriesRef.current = countriesRaw;

    const { data: availablePlatforms = [] } = useQuery({
        queryKey: ['companyPlatforms', companyID],
        queryFn: async () => {
            const res = await axios.get(
                `/Company/${companyID}/Platforms/Available`,
                { params: { AddressManagedOnly: true } },
            );
            return res.data || [];
        },
        enabled: Boolean(companyID),
    });

    const [selectedPlatforms, setSelectedPlatforms] = useState([]);
    const [showValidationError, setShowValidationError] = useState(false);
    const [selectedTab, setSelectedTab] = useState(0);
    const [isSaving, setIsSaving] = useState(false);

    const { ref } = usePlacesWidget({
        apiKey: secret.googleAutocomplete.testKey,
        onPlaceSelected: (place) =>
            transformAddress(place, countriesRef.current),
        options: {
            types: [], // this will screw the results, this lib sets types by default, which are actually optional on the maps SDK
        },
    });

    function onAddressCreate(e) {
        e.preventDefault();

        // Validate required field
        if (
            !addressFields.streetHouseNumber1 ||
            addressFields.streetHouseNumber1.trim() === ''
        ) {
            toast.error('Street House Number 1 is required');
            setShowValidationError(true);
            return;
        }

        // Clear validation error if field is filled
        setShowValidationError(false);

        setIsSaving(true);

        let compName;
        if (
            addressFields.companyName === undefined ||
            addressFields.companyName === null
        ) {
            compName = companyName;
        } else {
            compName = addressFields.companyName;
        }

        // Convert number fields to actual numbers
        const numberFields = [
            'building1stNumber',
            'buildingFloorNumber',
            'building2ndNumber',
            'streetHouseNumber1',
            'streetHouseNumber2',
        ];

        const processedFields = { ...addressFields };
        numberFields.forEach((field) => {
            if (processedFields[field] && processedFields[field] !== '') {
                const numValue = Number(processedFields[field]);
                if (!isNaN(numValue)) {
                    processedFields[field] = numValue;
                }
            }
        });

        const addressData = {
            companyID: companyID,
            companyName: compName,
            ...processedFields,
            // Add platform linking properties if platforms are selected
            ...(selectedPlatforms.length > 0 && {
                autoAddToNew: true,
                addToPlatforms: selectedPlatforms,
            }),
        };

        const addressFieldNames = {
            buildingFloorNumber: 'Building Floor Number',
            building1stNumber: 'Building 1st Number',
            building2ndNumber: 'Building 2nd Number',
            streetHouseNumber1: 'Street House Number 1',
            streetHouseNumber2: 'Street House Number 2',
            streetName: 'Street Name',
            suburb: 'Suburb',
            city: 'City',
            state: 'State',
            postCode: 'Post Code',
            country: 'Country',
            companyName: 'Company Name',
        };

        const getAddressErrorMessage = (error) => {
            const data = error?.response?.data;
            if (data?.errors && typeof data.errors === 'object') {
                const messages = Object.entries(data.errors)
                    .map(([field, msgs]) => {
                        const label = addressFieldNames[field] || field;
                        const msg = Array.isArray(msgs) ? msgs[0] : msgs;
                        return `${label}: ${msg}`;
                    })
                    .join('\n');
                if (messages) return messages;
            }
            return (
                data?.title ||
                data?.message ||
                'There was an error creating an address.'
            );
        };

        axios
            .post('/address', addressData)
            .then(() => {
                if (selectedPlatforms.length > 0) {
                    toast.success(
                        `Address created and linked to ${selectedPlatforms.length} platform(s) successfully`,
                    );
                } else {
                    toast.success('Address created successfully');
                }

                dispatch({
                    type: 'CHANGE_TABLE_LOADING',
                });
                setAddressAction('Get');
            })
            .catch((error) => {
                toast.error(getAddressErrorMessage(error));
            })
            .finally(() => {
                setIsSaving(false);
            });
    }
    const breadCrumbTrail = [
        { label: 'Address', path: ['', { index: 0 }] },
        { label: 'Add Address', path: '' },
    ];
    return (
        <>
            <Breadcrumb sx={breadCrumbStyling}>
                {breadCrumbTrail?.map((crumb, index) => (
                    <BreadcrumbItem key={index}>
                        {Array.isArray(crumb.path) ? (
                            <BreadcrumbLink
                                as={Link}
                                to={crumb.path[0]}
                                state={crumb.path[1]}
                                onClick={
                                    index === 0
                                        ? () => setAddressAction('Get')
                                        : undefined
                                }>
                                {crumb.label}
                            </BreadcrumbLink>
                        ) : (
                            <BreadcrumbLink
                                as={Link}
                                to={crumb.path}
                                onClick={
                                    index === 0
                                        ? () => setAddressAction('Get')
                                        : undefined
                                }>
                                {crumb.label}
                            </BreadcrumbLink>
                        )}
                    </BreadcrumbItem>
                ))}
            </Breadcrumb>

            <Box as="form" onSubmit={onAddressCreate}>
                <Tabs isLazy index={selectedTab} onChange={setSelectedTab}>
                    <TabList borderBottom="none">
                        <Tab
                            sx={{
                                ...customTabStylesCombineTheme,
                                color: darkMode
                                    ? 'rgba(255, 255, 255)'
                                    : 'inherit',
                            }}
                            _selected={{
                                ...selectedTabStylesCombineTheme,
                                textColor: darkMode
                                    ? 'rgba(255, 255, 255)'
                                    : 'inherit',
                            }}>
                            Details
                        </Tab>
                        {requiredScope >= 60 && (
                            <Tab
                                sx={{
                                    ...customTabStylesCombineTheme,
                                    color: darkMode
                                        ? 'rgba(255, 255, 255)'
                                        : 'inherit',
                                }}
                                _selected={{
                                    ...selectedTabStylesCombineTheme,
                                    textColor: darkMode
                                        ? 'rgba(255, 255, 255)'
                                        : 'inherit',
                                }}>
                                Custom
                            </Tab>
                        )}
                    </TabList>
                    <TabPanels padding={0}>
                        <TabPanel p={0}>
                            {/* Search address */}
                            <Card
                                borderRadius={15}
                                padding="0px 30px"
                                maxWidth={895}
                                ml={0}
                                mt={4}
                                bg={darkMode ? 'dark.tcap' : null}>
                                <Text
                                    fontSize="lg"
                                    sx={{
                                        fontWeight: 'bold',
                                        marginBottom: '5px',
                                        marginTop: '10px',
                                    }}>
                                    Search address
                                </Text>
                                <InputGroup
                                    width="775px"
                                    data-walkthroughid={
                                        addresses.ufSearchAddress
                                    }>
                                    <InputRightElement
                                        pointerEvents="none"
                                        // eslint-disable-next-line react/no-children-prop
                                        children={
                                            <SearchIcon color="#0000003b" />
                                        }
                                    />
                                    <Input
                                        placeholder=""
                                        customRef={ref}
                                        sx={{
                                            color: darkMode
                                                ? 'white'
                                                : '#000000de',
                                            borderColor: darkMode
                                                ? 'dark.tcap-borderGray'
                                                : '#0000003b',
                                            width: '775px',
                                        }}
                                    />
                                </InputGroup>

                                <Alert
                                    width="775px"
                                    status="info"
                                    sx={{ margin: '25px 0' }}>
                                    <AlertIcon />
                                    <AlertDescription>
                                        Address search will populate each field
                                        below, please ensure you check each
                                        field before submitting.
                                    </AlertDescription>
                                </Alert>
                                <AddressFields
                                    fields={addressFields}
                                    fieldHandler={setAddressField}
                                    countries={countries}
                                    showStreetHouseNumber1Error={
                                        showValidationError
                                    }
                                />
                            </Card>

                            {/* Platform Address Selection - Only show if platforms with address requirements are available */}
                            {availablePlatforms.length > 0 && (
                                <Card
                                    borderRadius={15}
                                    padding="30px"
                                    maxWidth={895}
                                    ml={0}
                                    mt={4}
                                    bg={darkMode ? 'dark.tcap' : null}>
                                    <Heading size="md" mb={4}>
                                        Create Platform Addresses (Optional)
                                    </Heading>
                                    <Text fontSize="sm" mb={4}>
                                        Link this address to platforms for
                                        integrated management. Once created, the
                                        address fields will become read-only.
                                    </Text>
                                    <VStack align="stretch" spacing={2} mb={4}>
                                        {availablePlatforms.map((platform) => (
                                            <Checkbox
                                                key={platform.id}
                                                isChecked={selectedPlatforms.includes(
                                                    platform.id,
                                                )}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedPlatforms([
                                                            ...selectedPlatforms,
                                                            platform.id,
                                                        ]);
                                                    } else {
                                                        setSelectedPlatforms(
                                                            selectedPlatforms.filter(
                                                                (id) =>
                                                                    id !==
                                                                    platform.id,
                                                            ),
                                                        );
                                                    }
                                                }}>
                                                {platform.name === 'Webex'
                                                    ? 'WebEx Local Gateway'
                                                    : platform.name}
                                            </Checkbox>
                                        ))}
                                    </VStack>
                                    {selectedPlatforms.length > 0 && (
                                        <Alert status="warning" mt={3}>
                                            <AlertIcon />
                                            <AlertDescription>
                                                <Text fontSize="sm">
                                                    Once this address is saved
                                                    with a platform link, the
                                                    address fields will become
                                                    read-only and cannot be
                                                    edited.
                                                </Text>
                                            </AlertDescription>
                                        </Alert>
                                    )}
                                </Card>
                            )}
                        </TabPanel>
                        {requiredScope >= 60 && (
                            <TabPanel p={0}>
                                <AddressCustomAttributes
                                    darkMode={darkMode}
                                    addressFields={addressFields}
                                    setAddressField={setAddressField}
                                />
                            </TabPanel>
                        )}
                    </TabPanels>
                </Tabs>

                <Box sx={{ marginBottom: '25px' }}></Box>

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                    }}>
                    <div
                        style={{
                            border: 'none',
                            textAlign: 'center',
                            justifyContent: 'center',
                        }}>
                        <Button
                            type="submit"
                            data-walkthroughid={addresses.ufSaveButton}
                            className="h-10 min-w-[5rem] font-semibold"
                            isLoading={isSaving}
                            loadingText="Saving"
                            isDisabled={isSaving}
                            sx={{ width: 142 }}>
                            Save
                        </Button>
                        <div
                            style={{
                                marginLeft: 30,
                                display: 'inline',
                            }}>
                            <Button
                                className="secondary h-10 min-w-[5rem] font-semibold"
                                variant="outline"
                                background="white"
                                sx={{ width: 142 }}
                                data-walkthroughid={addresses.ufBackButton}
                                onClick={() => {
                                    dispatch({
                                        type: 'CHANGE_TABLE_LOADING',
                                    });
                                    setAddressAction('Get');
                                }}>
                                Back
                            </Button>
                        </div>
                    </div>
                </Box>
            </Box>
        </>
    );
}
